/* eslint-disable complexity */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'design-system/styles';
import { useMutation, useQuery, UseQueryResult } from 'react-query';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { CircularProgress } from 'design-system';
import { IRule, IRuleExecution } from '@scale/shared/types/ruleEngine/rule';
import {
  StepConfiguration,
  StepDescriptions,
  StepLabels,
  StepTag,
  StepTags,
  StepTypes,
} from '@scale/shared/types/ruleEngine/step';
import { IQuery, IQueryExecution } from '@scale/shared/types/query';
import {
  TextField,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Modal,
  Autocomplete,
  Typography,
  Button,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Switch,
  Card,
  Chip,
} from '@mui/material';
import SAlert from 'react-s-alert';
import CodeMirror, { EditorDoc } from 'react-scale/corp/components/CodeMirror';
import {
  ParameterType,
  ValueType,
  ExecutionParameterValues,
  ExecutionStatus,
  IExecutionParameter,
  AllExecutionParameters,
} from '@scale/shared/types/execution';
import {
  formatQueryContent,
  getQueryInputKeys,
  getQueryListInputKeys,
  hasValue,
  isStepComplete,
  ruleChanges,
  getStepError,
  convertDynamicCorpInputs,
  convertDynamicCorpInputValue,
  getStepTags,
} from '@scale/shared/corp/ruleEngine';
import { IQueueMetricForm } from '@scale/shared/types/qarCorp';
import {
  DynamicCorpInputTypes,
  DynamicCorpPage as DynamicCorpPageNames,
} from '@scale/shared/corp/constants';
import DynamicCorpPage from '../DynamicCorpPage';
import { IUserJson } from '@scale/shared/types/user';
import {
  DynamicCorpParams,
  EnabledDynamicCorpParams,
} from '@scale/shared/corp/dynamicCorpToolManager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import QMLoadingPage from 'react-scale/corp/components/QM/QMLoadingPage';
import ExecutionParameter from 'react-scale/corp/components/RuleEngine/ExecutionParameter';
import RuleExecutionTable from 'react-scale/corp/components/RuleEngine/RuleExecutionTable';
import QueryResult from 'react-scale/corp/components/RuleEngine/QueryResult';
import { Steps } from '@scale/shared/types/ruleEngine/stepList';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import _ from 'lodash';

interface QueryItemProps {
  query: IQuery;
  setQuery?: (query?: IQuery) => void;
  projectId?: string;
}

const QueryItem = (props: QueryItemProps) => {
  const classes = styles();
  const { query, setQuery, projectId } = props;
  const [queryContent, setQueryContent] = useState<EditorDoc>(query.content || '\n\n\n\n');
  const [resultModalOpen, setResultModalOpen] = useState<boolean>(false);
  const [queryExecution, setQueryExecution] = useState<IQueryExecution | null>(null);

  const { data: fetchedQueryExecution } = useQuery<IQueryExecution>(
    [`QueryExecution`, queryExecution?._id],
    () => apiFetch(null, `corp-api/query/${queryExecution?._id}`),
    {
      enabled: Boolean(queryExecution?._id) && queryExecution?.status === ExecutionStatus.Running,
      refetchInterval: 1000,
    },
  );

  useEffect(() => {
    if (fetchedQueryExecution) {
      setQueryExecution(fetchedQueryExecution);
    }
  }, [fetchedQueryExecution]);

  useEffect(() => {
    if (setQuery) {
      if (queryExecution && queryExecution.status === ExecutionStatus.Completed) {
        setQuery({
          parameterTypes: queryExecution.query.parameterTypes,
          content: queryExecution.query.content,
          outputTypes: queryExecution.query.outputTypes,
        });
      }
    }
  }, [setQuery, queryExecution]);

  useEffect(() => {
    if (query && setQuery && query.content !== queryContent.toString()) {
      const currentQueryKeys: string[] = getQueryInputKeys(queryContent.toString());
      const currentQueryListKeys = getQueryListInputKeys(queryContent.toString());
      const inputs = query.parameterTypes;
      const newInputs: Record<string, AllExecutionParameters> = {};
      currentQueryListKeys.forEach(key => {
        const previousInput = inputs[key];
        newInputs[key] = previousInput ?? {
          type: ParameterType.Multiple,
          valueType: ValueType.Number,
          value: [],
          key,
        };
      });
      currentQueryKeys.forEach(key => {
        const previousInput = inputs[key];
        newInputs[key] = previousInput ?? {
          type: ParameterType.Single,
          valueType: ValueType.Number,
          key,
        };
      });
      setQueryExecution(null);
      setQuery({
        ...query,
        content: queryContent.toString(),
        parameterTypes: newInputs,
        outputTypes: {},
      });
    }
  }, [queryContent, queryExecution, setQuery, query]);

  const invalidQuery = useMemo(() => {
    const stringContent = queryContent.toString();
    const prohibitedKeywordsRegex = new RegExp('\\b(insert|update|delete)\\b');
    const formattedQuery = formatQueryContent(stringContent, query.parameterTypes).toLowerCase();
    return prohibitedKeywordsRegex.test(formattedQuery) || formattedQuery.trim().length === 0;
  }, [queryContent, query.parameterTypes]);

  const unsavedChanges = useMemo(
    () =>
      Boolean(
        Object.keys(query.outputTypes).length === 0 &&
          queryContent.toString() !== queryExecution?.query.content,
      ),
    [queryContent, queryExecution, query],
  );

  const canTest = useMemo(
    () =>
      !invalidQuery &&
      unsavedChanges &&
      (!query.parameterTypes ||
        Object.keys(query.parameterTypes).every(key => hasValue(query.parameterTypes[key].value))),
    [invalidQuery, query.parameterTypes, unsavedChanges],
  );

  const testQueryMutation = useMutation<IQueryExecution, unknown, IQuery>(
    (query: IQuery) =>
      apiFetch<IQueryExecution>(null, `corp-api/query`, {
        method: 'POST',
        body: {
          ...query,
          content: queryContent.toString(),
          projectId,
        },
      }),
    {
      onSuccess: data => {
        setQueryExecution(data);
      },
      onError: error => {
        SAlert.error(
          `Failed to execute query: ${_.get(error, 'response.message') ?? _.get(error, 'message')}`,
        );
      },
    },
  );

  const testing = testQueryMutation.isLoading;

  return (
    <>
      <p style={{ margin: 0, marginTop: '.5rem', color: '#374151', fontSize: '.8rem' }}>Code</p>
      <CodeMirror
        className={classes.codeBox}
        language="sql"
        value={queryContent}
        readOnly={testing}
        onUpdateValue={(newValue: EditorDoc) => setQueryContent(newValue)}
      />
      <div
        style={{
          display: 'flex',
          paddingTop: '.7rem',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <Button
          onClick={() => testQueryMutation.mutate(query)}
          color="warning"
          variant="contained"
          disabled={!canTest || testing}
        >
          {testing ? (
            <>
              <CircularProgress
                size={'1.3rem'}
                style={{ marginRight: '.3rem' }}
                color={'secondary'}
              />
              Executing
            </>
          ) : (
            'Execute'
          )}{' '}
          Query
        </Button>
        {unsavedChanges && !testing && <p style={{ margin: 0, color: '#d32f2f' }}>Required</p>}
        {queryExecution && queryExecution.status !== ExecutionStatus.Running && (
          <>
            {queryExecution.status === ExecutionStatus.Errored && (
              <p style={{ color: '#d32f2f', fontSize: '.7rem', margin: 0 }}>Query has errored</p>
            )}
            {queryExecution.status === ExecutionStatus.Completed &&
            queryExecution.output &&
            queryExecution.output.length === 0 ? (
              <p style={{ color: '#d32f2f', fontSize: '.7rem', margin: 0 }}>
                Query is not outputting anything
              </p>
            ) : (
              <Button
                onClick={() => setResultModalOpen(true)}
                color={queryExecution.status === ExecutionStatus.Errored ? 'error' : 'info'}
              >
                View Result
              </Button>
            )}
            <Modal open={resultModalOpen} onClose={() => setResultModalOpen(false)}>
              <div
                style={{
                  position: 'absolute',
                  background: '#F3F4F6',
                  borderRadius: '1rem',
                  inset: '10%',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <p style={{ lineHeight: 1, margin: 0, fontWeight: 'bold' }}>Query Result</p>
                  <button
                    style={{
                      border: 'none',
                      cursor: 'pointer',
                      padding: '.5rem .75rem',
                      background: 'none',
                    }}
                    onClick={() => setResultModalOpen(false)}
                  >
                    <FontAwesomeIcon icon="times" />
                  </button>
                </div>
                <div
                  style={{
                    position: 'relative',
                    background: 'white',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      borderRadius: '1rem',
                      overflow: 'auto',
                      height: '100%',
                      width: '100%',
                      padding: '1rem',
                    }}
                  >
                    <QueryResult result={queryExecution} />
                  </div>
                </div>
              </div>
            </Modal>
          </>
        )}
      </div>
    </>
  );
};

const StepSelect: React.FC<{
  setStep: (step: StepConfiguration) => void;
}> = ({ setStep }) => {
  return (
    <Select
      size="small"
      sx={{
        marginBottom: '1rem',
      }}
      value="placeholder"
      onChange={ev => {
        if (ev.target.value !== 'placeholder') {
          const stepType = ev.target.value as StepTypes;
          setStep({
            ...(Steps[stepType] as StepConfiguration),
          });
        }
      }}
    >
      <MenuItem disabled value={'placeholder'} key={`placeholder-option`}>
        Add a step
      </MenuItem>
      {Object.values(StepTypes).map(stepType => (
        <MenuItem value={stepType} key={`${stepType}-option`}>
          {StepLabels[stepType]}
        </MenuItem>
      ))}
    </Select>
  );
};

export const StepItem: React.FC<{
  user: IUserJson;
  projectId?: string;
  step: StepConfiguration;
  previousOutputs?: Record<string, IExecutionParameter>;
  setStep: (step?: StepConfiguration) => void;
  isCompassStep?: boolean;
  forceTouched?: boolean;
}> = ({ user, projectId, step, previousOutputs, setStep, isCompassStep = false, forceTouched }) => {
  const [showFullOutput, setShowFullOutput] = useState<boolean>(false);
  const [showExtraConfiguration, setShowExtraConfiguration] = useState<boolean>(true);
  const [touched, setTouched] = useState<boolean>(false);

  function saveStep(step?: StepConfiguration) {
    setTouched(true);
    setStep(step);
  }

  const setQuery = useCallback(
    (query?: IQuery) => {
      if (query) {
        saveStep({
          ...step,
          ...query,
        });
      } else if (!query) {
        saveStep({
          ...Steps.query,
        });
      }
    },
    [step],
  );

  const {
    presetFields,
    valueFields,
  }: {
    presetFields: Record<string, string>;
    valueFields: ExecutionParameterValues;
  } = useMemo(() => {
    const presetFields: Record<string, string> = {};
    const valueFields: ExecutionParameterValues = {};
    Object.keys(step.parameterTypes).map(k => {
      const param = step.parameterTypes[k];
      if (param.input) {
        presetFields[k] = param.input;
      } else if (hasValue(param.value)) {
        valueFields[k] = param.value!;
      }
    });
    return { presetFields, valueFields };
  }, [step.parameterTypes]);

  const allMatchingOutputs = useMemo((): Record<string, string[]> => {
    const matches: Record<string, string[]> = {};
    if (previousOutputs && step.type === StepTypes.DynamicCorpTool) {
      Object.keys(step.parameterTypes).map(k => {
        const param = step.parameterTypes[k];
        matches[k] = [];
        Object.keys(previousOutputs).map(key => {
          const output = previousOutputs[key];
          if (output.type === param.type && output.valueType === param.valueType) {
            matches[k].push(key);
          }
        });
      });
    }
    return matches;
  }, [previousOutputs, step]);

  const outputs = useMemo(
    (): Record<string, IExecutionParameter> => ({
      ...(previousOutputs ?? {}),
      ...step.outputTypes,
    }),
    [step, previousOutputs],
  );

  const errorMessage: string | undefined = useMemo(() => {
    return getStepError(step);
  }, [step]);

  const setParameter = (key: string, parameter: IExecutionParameter): void => {
    saveStep({
      ...step,
      parameterTypes: {
        ...step.parameterTypes,
        [key]: parameter as any,
      },
    });
  };

  const getStepForm = useMemo(() => {
    switch (step.type) {
      case StepTypes.DynamicCorpTool:
        return (
          <div
            style={{
              padding: '.7rem',
              background: 'white',
              borderRadius: '1rem',
            }}
          >
            <Autocomplete
              disablePortal
              id="corpTool-select"
              sx={'tool' in step && step.tool ? { marginBottom: '.7rem' } : undefined}
              options={Object.keys(EnabledDynamicCorpParams)}
              value={(step as StepConfiguration<StepTypes.DynamicCorpTool>).tool}
              renderInput={params => <TextField {...params} size="small" label="Select a tool" />}
              getOptionLabel={key => {
                return (
                  DynamicCorpParams?.[key as DynamicCorpPageNames]?.pageTitle ??
                  `[Deprecated tool - Needs to be changed] ${key} `
                );
              }}
              onChange={(ev, value) => {
                const dynamicCorpParams = DynamicCorpParams[value as DynamicCorpPageNames];
                if (dynamicCorpParams) {
                  const parameters: Record<string, AllExecutionParameters> =
                    convertDynamicCorpInputs(dynamicCorpParams.inputs);
                  saveStep({
                    ...step,
                    tool: value as DynamicCorpPageNames,
                    parameterTypes: parameters,
                  });
                } else {
                  saveStep({
                    ...step,
                    tool: undefined,
                    parameterTypes: {},
                  });
                }
              }}
            />
            {'tool' in step && step.tool && (
              <DynamicCorpPage
                user={user}
                pageName={step.tool}
                presetFields={presetFields}
                queryParams={valueFields}
                embedded={true}
                onInputUpdate={(type, key, value) => {
                  const parameter = step.parameterTypes[key];
                  const convertedValue = convertDynamicCorpInputValue(type, value);
                  if (type === DynamicCorpInputTypes.MultiUserOrTeamSelect) {
                    if (value) {
                      const userIds: string[] = value.userIds ?? [];
                      const workerTeamIds: string[] = value.workerTeamIds ?? [];
                      const userIdsParameter = step.parameterTypes[`${key}_userIds`];
                      const workerTeamIdsParameter = step.parameterTypes[`${key}_workerTeamIds`];
                      if (userIdsParameter && workerTeamIdsParameter) {
                        saveStep({
                          ...step,
                          parameterTypes: {
                            ...step.parameterTypes,
                            [`${key}_userIds`]: {
                              ...step.parameterTypes[`${key}_userIds`],
                              value: userIds as any,
                              optional: true,
                            },
                            [`${key}_workerTeamIds`]: {
                              ...step.parameterTypes[`${key}_workerTeamIds`],
                              value: workerTeamIds as any,
                              optional: true,
                            },
                          },
                        });
                      }
                    }
                  } else if (parameter && parameter.value !== convertedValue) {
                    saveStep({
                      ...step,
                      parameterTypes: {
                        ...step.parameterTypes,
                        [key]: {
                          ...step.parameterTypes[key],
                          value: convertedValue as any,
                        },
                      },
                    });
                  }
                }}
                presetOptions={allMatchingOutputs}
                onInputPresetUpdate={(key, targetKey) => {
                  if (step.parameterTypes[key]) {
                    saveStep({
                      ...step,
                      parameterTypes: {
                        ...step.parameterTypes,
                        [key]: {
                          ...step.parameterTypes[key],
                          input: targetKey,
                        },
                      },
                    });
                  }
                }}
              />
            )}
            {touched && errorMessage && (
              <p style={{ color: '#d32f2f', fontSize: '.7rem', margin: 0, width: '100%' }}>
                {errorMessage}
              </p>
            )}
          </div>
        );
      default:
        return (
          <>
            {Object.keys(step.parameterTypes).length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'start',
                  alignItems: 'start',
                  gap: '.8rem',
                  padding: '1rem',
                  borderRadius: '8px',
                  background: 'white',
                }}
              >
                {Object.keys(step.parameterTypes).map(parameterKey => {
                  const parameter = step.parameterTypes[parameterKey];
                  return (
                    <ExecutionParameter
                      key={parameterKey}
                      parameterKey={parameterKey}
                      previousOutputs={previousOutputs}
                      parameter={parameter}
                      editMode={step.type === StepTypes.Query}
                      setParameter={setParameter}
                      isCompassParameter={isCompassStep}
                      forceTouched={forceTouched}
                    />
                  );
                })}
                {touched && errorMessage && step.type !== StepTypes.Query && (
                  <p style={{ color: '#d32f2f', fontSize: '.7rem', margin: 0, width: '100%' }}>
                    {errorMessage}
                  </p>
                )}
              </div>
            )}
            {step.type === StepTypes.Query && (
              <QueryItem
                query={step as StepConfiguration<StepTypes.Query>}
                setQuery={setQuery}
                projectId={projectId}
              />
            )}
          </>
        );
    }
  }, [step, presetFields, allMatchingOutputs, valueFields, previousOutputs]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '.7rem',
      }}
    >
      <div
        style={{
          ...(isCompassStep
            ? {}
            : {
                borderRadius: '8px',
                background: '#F3F4F6',
                padding: '1rem',
                paddingTop: '.5rem',
              }),
        }}
      >
        {!isCompassStep && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <div>
                <IconButton
                  onClick={() => setShowExtraConfiguration(prev => !prev)}
                  color={showExtraConfiguration ? 'primary' : 'default'}
                >
                  <FontAwesomeIcon icon="cog" />
                </IconButton>
              </div>
              <p style={{ lineHeight: 1, margin: 0, fontWeight: 'bold' }}>
                {StepLabels[step.type]}
              </p>
              {StepTags[step.type] && (
                <p
                  style={{
                    lineHeight: 1.1,
                    fontSize: '.8rem',
                    margin: 0,
                    marginLeft: '.5rem',
                    padding: '.3rem .5rem',
                    background: '#c4ddf5',
                    textTransform: 'capitalize',
                    borderRadius: '1rem',
                  }}
                >
                  {StepTags[step.type]}
                </p>
              )}
            </div>
            <IconButton onClick={() => setStep(step.onSuccess)}>
              <FontAwesomeIcon icon="times" />
            </IconButton>
          </div>
        )}
        {showExtraConfiguration && !isCompassStep && (
          <div
            style={{
              background: '#f5faff',
              borderRadius: '8px',
              marginBottom: '1rem',
              padding: '.7rem',
              display: 'flex',
              gap: '.8rem',
            }}
          >
            <div>
              <p
                style={{
                  margin: 0,
                  lineHeight: 1.5,
                  color: '#374151',
                  fontSize: '.8rem',
                }}
              >
                Require approval
              </p>
              <Switch
                checked={step.requiresApproval}
                onChange={(ev, checked) =>
                  setStep({
                    ...step,
                    requiresApproval: checked,
                  })
                }
              />
            </div>
          </div>
        )}
        {StepDescriptions[step.type] && !isCompassStep && (
          <p style={{ margin: '0 .5rem .5rem .5rem' }}>{StepDescriptions[step.type]}</p>
        )}
        {getStepForm}
        {step.outputTypes && Object.keys(step.outputTypes).length > 0 && !isCompassStep && (
          <>
            <p style={{ marginBottom: 0, color: '#374151', fontSize: '.8rem' }}>Output Types</p>
            <div
              style={{
                display: 'flex',
                gap: '.7rem',
                flexWrap: 'wrap',
              }}
            >
              {Object.keys(step.outputTypes)
                .slice(0, 4)
                .map(key => (
                  <ExecutionParameter key={key} parameterKey={key} parameter={outputs[key]} />
                ))}
              {Object.keys(step.outputTypes).length > 4 && (
                <>
                  {showFullOutput &&
                    Object.keys(step.outputTypes)
                      .slice(4)
                      .map(key => (
                        <ExecutionParameter key={key} parameterKey={key} parameter={outputs[key]} />
                      ))}
                  <Button
                    variant="outlined"
                    style={{ margin: '0 auto', width: '100%' }}
                    onClick={() => setShowFullOutput(prev => !prev)}
                  >
                    {showFullOutput ? 'Hide' : 'Show'} {Object.keys(step.outputTypes).length - 4}{' '}
                    {!showFullOutput && 'more'} items
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </div>
      {!isCompassStep && (
        <>
          {step.onSuccess ? (
            <StepItem
              projectId={projectId}
              user={user}
              step={step.onSuccess}
              previousOutputs={outputs}
              setStep={newStep => setStep({ ...step, onSuccess: newStep })}
            />
          ) : (
            <StepSelect setStep={newStep => setStep({ ...step, onSuccess: newStep })} />
          )}
        </>
      )}
    </div>
  );
};

type TriggerType = 'schedule' | 'metric' | 'rule';

const QMRuleDetailsPage: React.FC<{
  user: IUserJson;
  projectId: string;
  rule: IRule;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
}> = ({ user, projectId, rule }) => {
  const [localRule, setLocalRule] = useState<IRule>(rule);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [executing, setExecuting] = useState<boolean>(true);
  const [changelogOpen, setChangelogOpen] = useState<boolean>(false);
  const [selectedPage, setSelectedPage] = useState<number>(0);
  const classes = styles();
  const router = useRouter();
  const { action } = router.query;
  const [selectedTab, setSelectedTab] = useState<number>(action && action === 'approval' ? 3 : 0);
  const [enableTimeout, setEnableTimeout] = useState<boolean>(
    Boolean(localRule.timeoutDurationMinutes),
  );
  const enableTimoutModification = useUnleashFeatureFlag(
    'auto-ops-enable-timeout-duration-modification',
    { projectId },
    false,
  );

  const [triggerType, setTriggerType] = useState<TriggerType>(
    rule.trigger.metric ? 'metric' : rule.trigger.rule ? 'rule' : 'schedule',
  );

  const enableEvery10MinRules = useUnleashFeatureFlag(
    'enable-every-10-min-rules',
    { projectId },
    false, // default to disabled
  );

  // Computed rule execution type - eliminates duplicate business logic
  const ruleExecutionType =
    localRule.trigger.every10Mins === true
      ? 'every10mins'
      : localRule.trigger.hour === null
      ? 'hourly'
      : 'specificHour';

  const changes = useMemo(() => {
    return ruleChanges(rule._id ? rule : undefined, localRule);
  }, [rule, localRule]);

  const { hasBasicInfo, hasTrigger, canSubmit } = useMemo(() => {
    const hasBasicInfo = Boolean(localRule.name && localRule.description);
    const hasTrigger = Boolean(
      (localRule.trigger.day.length > 0 &&
        (hasValue(localRule.trigger.hour) || localRule.trigger.hour === null)) ||
        localRule.trigger.metric ||
        localRule.trigger.rule,
    );
    const hasStep = Boolean(localRule.step && isStepComplete(localRule.step));
    return {
      hasBasicInfo,
      hasTrigger,
      canSubmit: Boolean(hasBasicInfo && hasTrigger && hasStep),
    };
  }, [localRule]);

  const needsApproval = useMemo(() => {
    return Object.keys(changes).some(ct =>
      changes[ct as keyof typeof changes].some(c => c.needsApproval),
    );
  }, [changes]);

  const defaultTags: StepTag[] = useMemo(() => {
    return localRule.step ? getStepTags(localRule.step) : [];
  }, [localRule]);

  function useFetchMetrics(): UseQueryResult<IQueueMetricForm[]> {
    return useQuery<IQueueMetricForm[], Error>(
      'RuleQueueMetrics',
      () => apiFetch(null, `corp-api/qar_corp/metrics`),
      {
        staleTime: Infinity,
      },
    );
  }
  const { data: metrics, isLoading: loadingMetrics } = useFetchMetrics();

  const categorizedMetrics = useMemo(() => {
    return metrics
      ? metrics.filter(
          m =>
            !m.root &&
            m.src &&
            m.src.type === 'query' &&
            metrics.some(metric => metric.submetrics && metric.submetrics.includes(m._id)),
        )
      : [];
  }, [metrics]);

  const selectedMetric = useMemo(() => {
    return metrics && metrics.length > 0 && localRule.trigger.metric
      ? metrics.find(m => m._id === localRule.trigger.metric)
      : undefined;
  }, [metrics, localRule]);

  function useFetchProjectRules() {
    return useQuery<IRule[], Error>(
      ['CorpProjectRules', projectId],
      () => apiFetch(null, `corp-api/rules?projectId=${projectId}`),
      {
        staleTime: Infinity,
      },
    );
  }
  const { data: projectRules, isLoading: loadingProjectRules } = useFetchProjectRules();

  const ruleExecutionsKey = ['CorpRuleExecutions', selectedPage.toString()];

  function useFetchRuleExecutions() {
    return useQuery<IRuleExecution[], Error>(
      ruleExecutionsKey,
      () => apiFetch(null, `corp-api/rules/${rule._id}/executions?page=${selectedPage}`),
      {
        enabled: Boolean(rule._id) && executing,
        refetchInterval: 5000,
      },
    );
  }
  const { data: executions, isLoading: loadingExecutions } = useFetchRuleExecutions();

  useEffect(() => {
    setExecuting(true);
  }, [selectedPage]);

  useEffect(() => {
    const hasRunningExecution: boolean = Boolean(
      executions && executions.length > 0 && executions[0].status === ExecutionStatus.Running,
    );
    if (rule._id && (!executions || hasRunningExecution)) {
      setExecuting(true);
    } else {
      setExecuting(false);
    }
  }, [executions]);

  const postRule = async (): Promise<void> => {
    setSaving(true);
    try {
      const response = await apiFetch<IRule>(null, `corp-api/rules`, {
        method: 'POST',
        body: localRule,
      });
      if (response._id) {
        if (rule._id) {
          router.reload();
        } else {
          router.push(`/corp/qm/queue/${projectId}/rules/${response._id}`);
        }
      }
      setSaving(false);
    } catch (error) {
      if (error.response.message) {
        setErrorMessage(error.response.message);
      }
      setSaving(false);
    }
  };

  const executeRule = async (): Promise<void> => {
    setExecuting(true);
    try {
      await apiFetch<IRule>(null, `corp-api/rules/${rule._id}/execute`, {
        method: 'POST',
      });
      setExecuting(true);
    } catch (error) {
      if (error.response.message) {
        setErrorMessage(error.response.message);
      }
      setExecuting(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        position: 'relative',
      }}
    >
      <div
        style={{
          width: '100%',
          padding: '16px',
          paddingLeft: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <h3 style={{ margin: 0 }}>
            {`Rule Engine > ${rule.approved ? '[Approved]' : '[Draft]'} ${
              rule._id ? rule.name : 'New Rule'
            }`}
          </h3>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            {selectedTab === 3 && (
              <Button
                disabled={
                  executing ||
                  (executions &&
                    executions.length > 0 &&
                    executions[0].status === ExecutionStatus.Running)
                }
                onClick={() => executeRule()}
                color="warning"
                variant="contained"
              >
                Execute Rule
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => router.push(router.asPath.slice(0, router.asPath.lastIndexOf('/')))}
            >
              Go Back
            </Button>
          </div>
        </div>
        <p style={{ margin: 0 }}>Automate workflows and execute tools based on events</p>
        <Tabs
          value={selectedTab}
          style={{ width: '100%' }}
          onChange={(ev, val) => setSelectedTab(val)}
        >
          <Tab label="Basic Information" />
          <Tab label="Trigger" disabled={!hasBasicInfo} />
          <Tab label="Workflow" disabled={!hasTrigger} />
          {rule._id && <Tab label="Executions" />}
        </Tabs>
      </div>
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '1rem',
          paddingTop: 0,
        }}
      >
        {selectedTab !== 3 ? (
          <Card
            style={{
              background: 'white',
              padding: '1rem',
              width: '100%',
              maxWidth: '1000px',
              margin: '1rem auto',
              height: 'auto',
            }}
          >
            {localRule && selectedTab === 0 ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                <TextField
                  name="name"
                  size="small"
                  type="text"
                  label="Name"
                  required
                  error={Boolean(rule.name && !localRule.name)}
                  value={localRule.name || ''}
                  onChange={e => setLocalRule(prev => ({ ...prev, name: e.target.value }))}
                />
                <TextField
                  name="description"
                  multiline
                  rows={4}
                  size="small"
                  label="Description"
                  required
                  error={Boolean(rule.description && !localRule.description)}
                  value={localRule.description || ''}
                  onChange={e => setLocalRule(prev => ({ ...prev, description: e.target.value }))}
                />
                <div>
                  <p style={{ margin: 0, color: '#374151', fontSize: '.8rem' }}>Tags</p>
                  <div style={{ display: 'flex', gap: '.5rem', textTransform: 'capitalize' }}>
                    {Object.values(StepTag).map(tag =>
                      localRule.tags.includes(tag) ? (
                        <Chip
                          label={tag}
                          color="primary"
                          disabled={defaultTags.includes(tag)}
                          onDelete={() =>
                            setLocalRule(prev => ({
                              ...prev,
                              tags: prev.tags.filter(t => t !== tag),
                            }))
                          }
                        />
                      ) : (
                        <Chip
                          label={tag}
                          variant="outlined"
                          onClick={() =>
                            setLocalRule(prev => ({
                              ...prev,
                              tags: [...prev.tags, tag],
                            }))
                          }
                        />
                      ),
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Switch
                    checked={localRule.flagErrors}
                    onChange={(ev, checked) =>
                      setLocalRule(prev => ({
                        ...prev,
                        flagErrors: checked,
                      }))
                    }
                  />
                  Flag Failures to Creator
                </div>
                {enableTimoutModification && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Switch
                        checked={enableTimeout}
                        onChange={(ev, checked) => {
                          setEnableTimeout(checked);
                          if (!checked) {
                            setLocalRule(prev => ({
                              ...prev,
                              timeoutDurationMinutes: undefined,
                              retryIntervalMinutes: undefined,
                            }));
                          }
                        }}
                      />
                      Enable retries on queries
                    </div>
                    {enableTimeout && (
                      <div>
                        <p style={{ margin: 0, color: '#374151', fontSize: '.8rem' }}>
                          Queries will be retried every {localRule.retryIntervalMinutes} minutes
                          until the execution is completed or until{' '}
                          {localRule.timeoutDurationMinutes} minutes have passed.
                        </p>
                        <div
                          style={{
                            width: '100%',
                            padding: '16px',
                            paddingLeft: 0,
                            gap: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                              value={localRule.retryIntervalMinutes}
                              onChange={e =>
                                setLocalRule(prev => ({
                                  ...prev,
                                  retryIntervalMinutes: Number(e.target.value),
                                }))
                              }
                              label="Retry Interval (Minutes)"
                              size="small"
                              required
                              helperText={`Min 4 minutes. Must be smaller than Timeout Duration`}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                              value={localRule.timeoutDurationMinutes}
                              onChange={e =>
                                setLocalRule(prev => ({
                                  ...prev,
                                  timeoutDurationMinutes: Math.min(120, Number(e.target.value)),
                                }))
                              }
                              label="Timeout Duration (Minutes)"
                              size="small"
                              required
                              helperText="Max 120 minutes"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {localRule.createdBy && (
                  <div>
                    <p style={{ margin: 0, color: '#374151', fontSize: '.8rem' }}>Created by</p>
                    <SmartLink id={localRule.createdBy} type={INamedObjectTypes.User} />
                  </div>
                )}
                {localRule.auditedBy && (
                  <div>
                    <p style={{ margin: 0, color: '#374151', fontSize: '.8rem' }}>
                      {localRule.approved ? 'Approved' : 'Rejected'} by
                    </p>
                    <SmartLink id={localRule.auditedBy} type={INamedObjectTypes.User} />
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                  {hasBasicInfo && (
                    <Button variant="contained" onClick={() => setSelectedTab(prev => prev + 1)}>
                      Continue
                    </Button>
                  )}
                </div>
              </div>
            ) : selectedTab === 1 ? (
              <div>
                <div style={{ paddingBottom: '1rem' }}>
                  <p style={{ margin: 0, color: '#374151', fontSize: '.8rem' }}>Type</p>
                  <Select
                    size="small"
                    value={triggerType}
                    onChange={e => {
                      setLocalRule(prev => ({
                        ...prev,
                        trigger: {
                          ...prev.trigger,
                          metric: undefined,
                          rule: undefined,
                          day: e.target.value === 'schedule' ? [0] : [],
                          hour: undefined,
                          minute: undefined,
                          every10Mins: undefined,
                        },
                      }));
                      setTriggerType(e.target.value as TriggerType);
                    }}
                  >
                    <MenuItem value="schedule">Schedule</MenuItem>
                    <MenuItem value="metric">QAR Metric on issue</MenuItem>
                    <MenuItem value="rule">Another Rule</MenuItem>
                  </Select>
                </div>
                {triggerType === 'schedule' ? (
                  <div>
                    <div style={{ paddingBottom: '1rem', display: 'flex', gap: '1rem' }}>
                      <div>
                        <p style={{ margin: 0, color: '#374151', fontSize: '.8rem' }}>Repeats</p>
                        <ToggleButtonGroup
                          value={localRule.trigger.day}
                          size="small"
                          color="primary"
                          classes={{
                            grouped: classes.toggleButton,
                          }}
                          onChange={(ev, val: number[]) =>
                            setLocalRule(prev => ({
                              ...prev,
                              trigger: {
                                ...prev.trigger,
                                day: val[val.length - 1] === 0 ? [0] : val.filter(v => v !== 0),
                              },
                            }))
                          }
                        >
                          {['Daily', 'Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(
                            (dayLabel, dayIndex) => (
                              <ToggleButton key={dayLabel} value={dayIndex}>
                                {dayLabel}
                              </ToggleButton>
                            ),
                          )}
                        </ToggleButtonGroup>
                      </div>
                      <div>
                        <p style={{ margin: 0, color: '#374151', fontSize: '.8rem' }}>At (UTC)</p>
                        <Select
                          value={
                            ruleExecutionType === 'every10mins'
                              ? 'every10mins'
                              : ruleExecutionType === 'hourly'
                              ? 'hourly'
                              : `${localRule.trigger.hour}`
                          }
                          size="small"
                          placeholder="Hour"
                          onChange={e => {
                            setLocalRule(prev => {
                              let newHour: number | null, newMinute: number | null;
                              const newTrigger: any = { ...prev.trigger };

                              if (e.target.value === 'hourly') {
                                newHour = null;
                                newMinute = null; // Backend will assign random minute
                                delete newTrigger.every10Mins;
                              } else if (e.target.value === 'every10mins') {
                                newHour = null;
                                newMinute = null;
                                newTrigger.every10Mins = true;
                              } else {
                                newHour = Number(e.target.value);
                                newMinute = prev.trigger.minute ?? 0;
                                delete newTrigger.every10Mins;
                              }

                              newTrigger.hour = newHour;
                              newTrigger.minute = newMinute;

                              return {
                                ...prev,
                                trigger: newTrigger,
                              };
                            });
                          }}
                        >
                          {Array.from({ length: 24 }, (_, hour) => (
                            <MenuItem value={`${hour}`} key={`${hour}`}>
                              {hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                            </MenuItem>
                          ))}
                          <MenuItem value={'hourly'}>Hourly</MenuItem>
                          {enableEvery10MinRules && (
                            <MenuItem value={'every10mins'}>Every 10 minutes</MenuItem>
                          )}
                        </Select>
                      </div>
                    </div>
                    <div style={{ marginTop: '2rem', color: '#6B7280', fontSize: '.2rem' }}>
                      <Typography>
                        {ruleExecutionType === 'every10mins'
                          ? 'Rules will execute every 10 minutes'
                          : ruleExecutionType === 'hourly'
                          ? 'Rules are randomly allotted an execution minute in each hour'
                          : 'Rules are randomly allotted an execution minute in the specified hour'}
                      </Typography>
                    </div>
                  </div>
                ) : triggerType === 'metric' ? (
                  <Autocomplete
                    disablePortal
                    style={{ marginBottom: '1rem' }}
                    loading={loadingMetrics}
                    loadingText="Loading metrics"
                    noOptionsText="No metrics found"
                    options={categorizedMetrics}
                    value={selectedMetric}
                    renderInput={params => (
                      <TextField {...params} size="small" label="Select a metric" />
                    )}
                    getOptionLabel={metric => metric.name}
                    onChange={(ev, metric) =>
                      metric &&
                      setLocalRule(prev => ({
                        ...prev,
                        trigger: {
                          ...prev.trigger,
                          metric: metric._id,
                        },
                      }))
                    }
                  />
                ) : (
                  <>
                    <p style={{ margin: 0, color: '#374151', fontSize: '.8rem' }}>Rule</p>
                    <Select
                      size="small"
                      onChange={e =>
                        setLocalRule(prev => ({
                          ...prev,
                          trigger: {
                            ...prev.trigger,
                            rule: e.target.value as string,
                          },
                        }))
                      }
                      value={localRule.trigger.rule || ''}
                    >
                      {loadingProjectRules ? (
                        <MenuItem>
                          <CircularProgress />
                        </MenuItem>
                      ) : (
                        projectRules &&
                        projectRules
                          .filter(pr => Boolean(!rule._id || rule._id !== pr._id))
                          .map(rule => (
                            <MenuItem value={rule._id} key={rule._id}>
                              {rule.name}
                            </MenuItem>
                          ))
                      )}
                    </Select>
                  </>
                )}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'end',
                    gap: '1rem',
                  }}
                >
                  <Button onClick={() => setSelectedTab(prev => prev - 1)}>Back</Button>
                  {hasTrigger && (
                    <Button variant="contained" onClick={() => setSelectedTab(prev => prev + 1)}>
                      Continue
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              selectedTab === 2 && (
                <div>
                  {localRule.step ? (
                    <StepItem
                      user={user}
                      projectId={projectId}
                      step={localRule.step}
                      setStep={step =>
                        setLocalRule(prev => ({
                          ...prev,
                          step: step as (typeof prev)['step'],
                          tags: step
                            ? [...new Set([...(prev.tags ?? []), ...getStepTags(step)])]
                            : prev.tags ?? [],
                        }))
                      }
                    />
                  ) : (
                    <StepSelect
                      setStep={step =>
                        setLocalRule(prev => ({
                          ...prev,
                          step: step as (typeof prev)['step'],
                          tags: step
                            ? [...new Set([...(prev.tags ?? []), ...getStepTags(step)])]
                            : prev.tags ?? [],
                        }))
                      }
                    />
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'end',
                      gap: '1rem',
                    }}
                  >
                    <Button onClick={() => setSelectedTab(prev => prev - 1)}>Back</Button>
                  </div>
                </div>
              )
            )}
          </Card>
        ) : (
          <>
            {executions && (selectedPage > 0 || executions.length > 0) ? (
              <RuleExecutionTable
                ruleExecutionsKey={ruleExecutionsKey}
                executions={executions}
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
              />
            ) : loadingExecutions ? (
              <QMLoadingPage />
            ) : (
              <>
                <Typography>No executions found for this rule</Typography>
                <Button color="warning" disabled={executing} onClick={() => executeRule()}>
                  Execute Rule
                </Button>
              </>
            )}
          </>
        )}
      </div>
      {localRule &&
        selectedTab !== 3 &&
        Object.keys(changes).some(
          changeType => changes[changeType as keyof typeof changes].length > 0,
        ) && (
          <div
            style={{
              maxWidth: '1000px',
              width: '100%',
              position: 'absolute',
              bottom: 0,
              left: 0,
              zIndex: 10,
              right: 0,
              margin: '0 auto',
              top: 'auto',
              padding: '1rem',
              background: !canSubmit ? '#fcebe8' : needsApproval ? '#fff8e6' : '#ffffff',
              borderRadius: '1rem 1rem 0 0',
              border: `1px solid ${
                !canSubmit ? '#fcebe8' : needsApproval ? 'rgb(255, 153, 0)' : 'rgb(106, 93, 249)'
              }`,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'end',
                gap: '1rem',
              }}
            >
              {rule._id && (
                <Button
                  onClick={() => setChangelogOpen(prev => !prev)}
                  color={!canSubmit || needsApproval ? 'warning' : 'primary'}
                  style={{ flex: 1 }}
                  variant="outlined"
                >
                  {`${changelogOpen ? 'Hide' : 'Show'} changes`}
                </Button>
              )}
              <Button
                onClick={() => postRule()}
                disabled={saving || !canSubmit}
                color={needsApproval ? 'warning' : 'primary'}
                variant="contained"
              >
                {needsApproval ? 'Submit rule for approval' : 'Save rule'}
              </Button>
            </div>
            {changelogOpen && (
              <div
                style={{
                  borderRadius: '8px',
                  background: 'white',
                  padding: '.5rem',
                  marginTop: '1rem',
                }}
              >
                {Object.keys(changes)
                  .filter(c => changes[c as keyof typeof changes].length > 0)
                  .map(changeType => {
                    return (
                      <div key={changeType}>
                        {changeType !== 'custom' && (
                          <p style={{ margin: 0, textTransform: 'capitalize' }}>
                            <b>{changeType.split('_').join(' ')}</b>
                          </p>
                        )}
                        <ul style={{ margin: 0, paddingLeft: '24px' }}>
                          {changes[changeType as keyof typeof changes].map(
                            (change, changeIndex) => (
                              <li
                                key={changeIndex}
                                style={{
                                  margin: 0,
                                  background: change.requiredAndEmpty
                                    ? '#fcebe8'
                                    : change.needsApproval
                                    ? '#fff8e6'
                                    : '#ffffff',
                                  borderRadius: '8px',
                                  padding: '0 6px',
                                }}
                              >
                                {change.content}{' '}
                                {change.needsApproval && (
                                  <span style={{ color: 'grey', fontSize: '.8rem' }}>
                                    Requires approval
                                  </span>
                                )}
                                {change.requiredAndEmpty && (
                                  <span style={{ color: 'grey', fontSize: '.8rem' }}>Required</span>
                                )}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(undefined)}
      >
        <Alert onClose={() => setErrorMessage(undefined)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default QMRuleDetailsPage;

const styles = makeStyles<{ status?: ExecutionStatus }>({
  toggleButton: {
    minHeight: '40px',
  },
  codeBox: {
    border: '1px solid lightgrey',
    borderRadius: '0 8px 8px 0',
    '& .cm-editor': {
      borderRadius: '0 8px 8px 0',
    },
    background: 'white',
  },
  step: {
    boxShadow: '0px 10px 20px 0px #6B72801A; px -10px 20px 0px #6B72800D',
    background: 'white',
    borderRadius: '8px',
    paddingLeft: '.5rem',
    paddingRight: 'calc(1rem - 8px)',
    marginLeft: '-.5rem',
  },
  executionTable: {
    boxShadow: '0px 10px 20px 0px #6B72801A; px -10px 20px 0px #6B72800D',
    background: 'white',
    borderRadius: '1rem',
  },
  tableButton: {
    background: 'none',
    border: 'none',
    height: '33px',
    width: '33px',
    cursor: 'pointer',
    borderRadius: '2rem',
    '&:hover': {
      background: 'lightgrey',
    },
  },
  executionStepItemRow: props => ({
    '& .MuiTableCell-root': {
      background: () => {
        switch (props.status) {
          case ExecutionStatus.Completed:
            return '#e8fcf6';
          case ExecutionStatus.Errored:
            return '#fcebe8';
          case ExecutionStatus.Running:
            return '#e6efff';
          case ExecutionStatus.Pending:
            return '#f5ebff';
          default:
            return '#f7f7f7';
        }
      },
    },
  }),
  noBorderRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
});
